import { useEffect, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { startPointerDrag } from './pointerDrag.ts';

/** The panel's geometry, in viewport pixels. `null` anywhere a `Float` is
 *  expected means DOCKED. */
export type Float = { x: number; y: number; w: number; h: number };

/** Which edges the drag is pulling — any combination of n/s/e/w, as the
 *  handle's own class suffix spells it ('nw', 'se', 'e'…). */
export type ResizeDir = string;

// Floating-panel geometry constraints. Floating is the DEFAULT chat view; an
// explicit dock persists as the literal string 'docked' so it survives reloads.
const MIN_W = 320;
const MIN_H = 240;
const DEFAULT_W = 440;
const DEFAULT_H = 560;
const STORAGE_KEY = 'studio.chatFloat';

/** Enough of a Float to be worth clamping — the stored record's `w`/`h` have
 *  always been treated as optional (`f.w || DEFAULT_W`), so only `x` and `y`
 *  are required, exactly as the original check read. */
function isFloatish(v: unknown): v is { x: number; y: number; w?: number; h?: number } {
  return !!v && typeof v === 'object' && typeof (v as { x?: unknown }).x === 'number';
}

function defaultFloat(): Float {
  const w = Math.min(DEFAULT_W, window.innerWidth - 48);
  const h = Math.min(DEFAULT_H, window.innerHeight - 96);
  return {
    x: Math.max(8, window.innerWidth - w - 24),
    y: Math.max(8, window.innerHeight - h - 24),
    w,
    h,
  };
}

/**
 * The chat's pop-out window concern, lifted out of CommandBar so the component
 * stays about chat. Owns the `float` state ({ x, y, w, h } or null = docked),
 * its persistence, the ResizeObserver that records CSS `resize: both` drags,
 * and the edge/header pointer-drag handlers.
 *
 * Takes the section ref to observe. Returns a bare `popOut()` (geometry only) —
 * CommandBar wraps it to also open the conversation, so the open/onToggleOpen
 * plumbing stays in the component rather than leaking into this hook.
 *
 * `disabled` (phones, where the panel is the bottom of a stacked layout)
 * forces docked rendering without touching the persisted geometry — the
 * underlying state survives, so a rotate or window resize back to desktop
 * restores the user's floating panel exactly where it was.
 */
export function useFloatingPanel(sectionRef: RefObject<HTMLElement | null>, disabled = false) {
  const [float, setFloat] = useState<Float | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === 'docked') return null;
      // Narrowed, not asserted: this key has outlived more than one shape of
      // the panel, and a stale record must fall back rather than place the
      // window at NaN.
      const f: unknown = raw ? JSON.parse(raw) : null;
      if (!isFloatish(f)) return defaultFloat();
      // Clamp back on-screen in case the window shrank since last session.
      return {
        x: Math.min(Math.max(f.x, 0), Math.max(0, window.innerWidth - 280)),
        y: Math.min(Math.max(f.y, 0), Math.max(0, window.innerHeight - 160)),
        w: Math.min(f.w || DEFAULT_W, window.innerWidth),
        h: Math.min(f.h || 520, window.innerHeight),
      };
    } catch {
      return defaultFloat();
    }
  });

  const effFloat = disabled ? null : float;

  // Persist float geometry; CSS `resize: both` handles resizing, the
  // ResizeObserver just records the result. 'docked' is sticky — floating
  // is the default, so an explicit dock must survive reloads. Disabled
  // (phone) sessions never write — they'd record the stacked layout's
  // dimensions over the user's desktop geometry.
  useEffect(() => {
    if (disabled) return;
    localStorage.setItem(STORAGE_KEY, float ? JSON.stringify(float) : 'docked');
  }, [float, disabled]);

  useEffect(() => {
    if (!effFloat || !sectionRef.current) return;
    const el = sectionRef.current;
    const ro = new ResizeObserver(() => {
      setFloat((f) => {
        if (!f) return f;
        if (f.w === el.offsetWidth && f.h === el.offsetHeight) return f;
        return { ...f, w: el.offsetWidth, h: el.offsetHeight };
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [!!effFloat]); // eslint-disable-line react-hooks/exhaustive-deps

  function popOut() {
    if (disabled) return;
    const w = DEFAULT_W;
    const h = Math.min(DEFAULT_H, window.innerHeight - 80);
    setFloat({ x: window.innerWidth - w - 24, y: window.innerHeight - h - 24, w, h });
  }

  // Resize the floating panel from any edge or corner. The native CSS
  // resize handle only grows toward bottom-right — useless for a panel
  // anchored at the bottom-right of the screen.
  function onEdgeResizeStart(e: ReactPointerEvent<HTMLElement>, dir: ResizeDir) {
    e.preventDefault();
    e.stopPropagation();
    const start = { x: e.clientX, y: e.clientY };
    // Nothing to resize while docked; the handles aren't rendered then, so this
    // is a guard rather than a case.
    if (!float) return;
    const orig: Float = { ...float };
    startPointerDrag(e, {
      onMove(ev) {
        const dx = ev.clientX - start.x;
        const dy = ev.clientY - start.y;
        setFloat((f) => {
          if (!f) return f;
          let { x, y, w, h } = orig;
          if (dir.includes('e')) {
            w = Math.min(Math.max(orig.w + dx, MIN_W), window.innerWidth - orig.x - 8);
          }
          if (dir.includes('s')) {
            h = Math.min(Math.max(orig.h + dy, MIN_H), window.innerHeight - orig.y - 8);
          }
          if (dir.includes('w')) {
            x = Math.min(Math.max(orig.x + dx, 8), orig.x + orig.w - MIN_W);
            w = orig.x + orig.w - x;
          }
          if (dir.includes('n')) {
            y = Math.min(Math.max(orig.y + dy, 8), orig.y + orig.h - MIN_H);
            h = orig.y + orig.h - y;
          }
          return { x, y, w, h };
        });
      },
    });
  }

  // Drag the panel by its header. Pointer capture survives the preview iframe.
  function onFloatDragStart(e: ReactPointerEvent<HTMLElement>) {
    // header buttons stay clickable
    if (e.target instanceof Element && e.target.closest('button')) return;
    e.preventDefault();
    if (!float) return;
    const start = { x: e.clientX, y: e.clientY };
    const orig: Float = { ...float };
    startPointerDrag(e, {
      onMove(ev) {
        setFloat(
          (f) =>
            f && {
              ...f,
              x: Math.min(Math.max(orig.x + ev.clientX - start.x, 0), window.innerWidth - 120),
              y: Math.min(Math.max(orig.y + ev.clientY - start.y, 0), window.innerHeight - 60),
            },
        );
      },
    });
  }

  return { float: effFloat, setFloat, popOut, onEdgeResizeStart, onFloatDragStart };
}
